import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Field, Input, Section, Select, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import type { AttributionReport } from '../lib/types';
import { campaignUrl, UTM_MEDIUMS, UTM_SOURCES } from '../lib/utm';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Kampanyalar ve kaynaklar (F5-13): kampanya bağlantısı (UTM) oluşturucu ve siparişlerin kaynağa göre raporu.
 * Rapor yalnızca gerçek siparişlerden; kaynak bilgisi ziyaretçi çerez izni verdiyse siparişe yazılır.
 */
export function AttributionPage() {
  const { stores, storeId, setStoreId } = useStoreChoice();
  const store = stores.find((s) => s.id === storeId);
  const hosts = (store?.domains ?? [])
    .filter((d) => d.purpose === 'storefront')
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
    .map((d) => d.hostname);
  return (
    <PageFrame helpKey="page.attribution">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      <BuilderSection key={`b-${storeId}`} hosts={hosts} />
      {storeId && <ReportSection storeId={storeId} />}
    </PageFrame>
  );
}

function BuilderSection({ hosts }: { hosts: string[] }) {
  const t = useT();
  const [f, setF] = useState({
    url: hosts[0] ? `https://${hosts[0]}/` : '',
    source: '',
    medium: '',
    campaign: '',
    term: '',
    content: '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const url = useMemo(() => campaignUrl(f), [f]);
  const fields: Array<{ key: keyof typeof f; list?: string[]; required: boolean }> = [
    { key: 'source', list: UTM_SOURCES, required: true },
    { key: 'medium', list: UTM_MEDIUMS, required: true },
    { key: 'campaign', required: true },
    { key: 'term', required: false },
    { key: 'content', required: false },
  ];
  return (
    <Section
      title={t('admin.attribution.builderTitle')}
      description={t('admin.attribution.builderLead')}
      action={<HelpTip k="attribution.builder" />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('admin.attribution.url')}
          className="sm:col-span-2"
          help={<HelpTip k="attribution.url" />}
        >
          {(p) => (
            <Input
              {...p}
              type="url"
              value={f.url}
              placeholder="https://zootopia.com.tr/urun/..."
              onChange={(e) => set('url', e.target.value)}
            />
          )}
        </Field>
        {fields.map(({ key, list, required }) => (
          <Field
            key={key}
            label={t(`admin.attribution.fields.${key}`)}
            optionalLabel={required ? undefined : t('common.optional')}
            help={<HelpTip k={`attribution.${key}`} />}
          >
            {(p) => (
              <>
                <Input
                  {...p}
                  value={f[key]}
                  maxLength={100}
                  autoComplete="off"
                  list={list ? `utm-${key}` : undefined}
                  onChange={(e) => set(key, e.target.value)}
                />
                {list && (
                  <datalist id={`utm-${key}`}>
                    {list.map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                )}
              </>
            )}
          </Field>
        ))}
      </div>
      <Field label={t('admin.attribution.result')} className="mt-4">
        {(p) => (
          <div className="flex gap-2">
            <Input
              {...p}
              readOnly
              value={url ?? ''}
              placeholder={t('admin.attribution.resultEmpty')}
              className="font-mono text-[0.8125rem]"
            />
            <Button
              type="button"
              disabled={!url}
              onClick={() =>
                url &&
                void navigator.clipboard
                  .writeText(url)
                  .then(() => toast.success(t('admin.attribution.copied')))
              }
            >
              {t('admin.attribution.copy')}
            </Button>
          </div>
        )}
      </Field>
      <p className="mt-2 text-sm text-ink-3">{t('admin.attribution.builderNote')}</p>
    </Section>
  );
}

function ReportSection({ storeId }: { storeId: string }) {
  const t = useT();
  const fmt = useMoney();
  const [days, setDays] = useState('30');
  const [model, setModel] = useState<'last' | 'first'>('last');
  const [dimension, setDimension] = useState<'source_medium' | 'campaign'>('source_medium');
  const q = useQuery({
    queryKey: ['attribution', storeId, days, model, dimension],
    queryFn: () =>
      api.get<AttributionReport>(
        `/v1/admin/attribution?storeId=${storeId}&days=${days}&model=${model}&dimension=${dimension}`,
      ),
  });
  const label = (r: AttributionReport['rows'][number]) => {
    if (!r.consent) return t('admin.attribution.noConsent');
    if (!r.key)
      return dimension === 'campaign' ? t('admin.attribution.noCampaign') : t('admin.attribution.direct');
    return r.key;
  };
  return (
    <Section
      title={t('admin.attribution.reportTitle')}
      description={t('admin.attribution.reportLead')}
      action={<HelpTip k="attribution.report" />}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Field label={t('admin.attribution.period')}>
          {(p) => (
            <Select {...p} value={days} onChange={(e) => setDays(e.target.value)}>
              {['7', '30', '90', '365'].map((d) => (
                <option key={d} value={d}>
                  {t('admin.attribution.lastDays', { n: Number(d) })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.attribution.model')} help={<HelpTip k="attribution.model" />}>
          {(p) => (
            <Select {...p} value={model} onChange={(e) => setModel(e.target.value as typeof model)}>
              <option value="last">{t('admin.attribution.models.last')}</option>
              <option value="first">{t('admin.attribution.models.first')}</option>
            </Select>
          )}
        </Field>
        <Field label={t('admin.attribution.dimension')}>
          {(p) => (
            <Select
              {...p}
              value={dimension}
              onChange={(e) => setDimension(e.target.value as typeof dimension)}
            >
              <option value="source_medium">{t('admin.attribution.dimensions.source_medium')}</option>
              <option value="campaign">{t('admin.attribution.dimensions.campaign')}</option>
            </Select>
          )}
        </Field>
      </div>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.rows.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.attribution.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.attribution.reportTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">
                  {dimension === 'campaign'
                    ? t('admin.attribution.colCampaign')
                    : t('admin.attribution.colSource')}
                </th>
                <th className="px-3 py-2 text-start font-medium">
                  {dimension === 'campaign'
                    ? t('admin.attribution.colSource')
                    : t('admin.attribution.colMedium')}
                </th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.attribution.colOrders')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.attribution.colPaid')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.attribution.colRevenue')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.attribution.colRefunded')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.attribution.colNet')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r, i) => (
                <tr
                  key={`${r.key ?? ''}|${r.secondary ?? ''}|${r.consent}|${r.currency}|${i}`}
                  className="border-t border-line"
                >
                  <td className="px-3 py-2">
                    {r.key && r.consent ? r.key : <Badge tone="slate">{label(r)}</Badge>}
                  </td>
                  <td className="px-3 py-2 text-ink-2">{r.consent ? (r.secondary ?? '—') : '—'}</td>
                  <td className="px-3 py-2 text-end tabular-nums">{r.orders}</td>
                  <td className="px-3 py-2 text-end tabular-nums">{r.paidOrders}</td>
                  <td className="px-3 py-2 text-end tabular-nums">{fmt(r.revenueMinor, r.currency)}</td>
                  <td className="px-3 py-2 text-end tabular-nums">{fmt(r.refundedMinor, r.currency)}</td>
                  <td className="px-3 py-2 text-end font-medium tabular-nums">
                    {fmt(r.netMinor, r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-sm text-ink-3">{t('admin.attribution.reportNote')}</p>
    </Section>
  );
}
